// ============================================================================
// The Facility details card's field list, its sterile-split rule and its save,
// moved out of facility-detail.tsx unchanged (item 944) so Ask AI's building
// card asks the same fields and saves through the same door (TCSD-0188).
// ============================================================================

import { supabase } from '../../../shared/supabase';

// This map used to be keyed on the detail TABLE a facility_type routed to. The
// three tables are now one, so nothing but this map decides what a surgery
// centre is asked for and what a dialysis centre is asked for. A column offered
// to the wrong type SAVES instead of failing, so read it carefully rather than
// editing it quickly. Every key below is a real column on
// public.facility_details (verified 2026-09-17). DO NOT add num_pacu_bays.
export type DetailFieldSpec = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select';
  options?: Array<{ value: string; label: string }>;
  /** A stored 0 means "not yet recorded" for this field — see isPopulated. */
  zeroMeansUnknown?: boolean;
};

export const TOTAL_SQFT_SPEC: DetailFieldSpec = { key: 'total_sqft', label: 'Total sq ft', type: 'number' };

export const DETAIL_FIELD_SPECS: Record<string, DetailFieldSpec[]> = {
  Surgery_Center: [
    TOTAL_SQFT_SPEC,
    { key: 'num_or_suites',       label: 'OR suites',       type: 'number', zeroMeansUnknown: true },
    { key: 'num_procedure_rooms', label: 'Procedure rooms', type: 'number', zeroMeansUnknown: true },
    { key: 'num_pre_op_bays',     label: 'Pre-op bays',     type: 'number', zeroMeansUnknown: true },
  ],
  // ⛔ MOB is asked for the surgery-centre list, unchanged from when it shared
  // that table — what a MOB should be asked for is its own small piece of work.
  MOB: [
    TOTAL_SQFT_SPEC,
    { key: 'num_or_suites',       label: 'OR suites',       type: 'number', zeroMeansUnknown: true },
    { key: 'num_procedure_rooms', label: 'Procedure rooms', type: 'number', zeroMeansUnknown: true },
    { key: 'num_pre_op_bays',     label: 'Pre-op bays',     type: 'number', zeroMeansUnknown: true },
  ],
  Hospital: [
    TOTAL_SQFT_SPEC,
    { key: 'num_or_suites',       label: 'OR suites',       type: 'number', zeroMeansUnknown: true },
    { key: 'num_procedure_rooms', label: 'Procedure rooms', type: 'number', zeroMeansUnknown: true },
    { key: 'num_floors',          label: 'Floors',          type: 'number', zeroMeansUnknown: true },
    { key: 'num_patient_rooms',   label: 'Patient rooms',   type: 'number', zeroMeansUnknown: true },
    { key: 'num_icu_beds',        label: 'ICU beds',        type: 'number', zeroMeansUnknown: true },
    { key: 'emergency_dept_yn',   label: 'Emergency dept (Y/N)', type: 'select',
      options: [
        { value: 'Y', label: 'Y — has an ED' },
        { value: 'N', label: 'N — no ED' },
      ] },
  ],
  Dialysis: [
    TOTAL_SQFT_SPEC,
    { key: 'chair_count',          label: 'Chairs',              type: 'number', zeroMeansUnknown: true },
    { key: 'treatment_floor_sqft', label: 'Treatment floor sq ft', type: 'number' },
  ],
  // Other records its total square footage only (2026-09-17).
  Other: [TOTAL_SQFT_SPEC],
};

/** Sterile sq ft is asked only of a surgery centre; non-sterile is derived. */
export const hasSterileSplit = (facilityType: string | null | undefined): boolean =>
  facilityType === 'Surgery_Center';

/**
 * The card's ONE statement: the draft, with non_sterile_sqft recomputed in the
 * same patch whenever the split is touched — or a refusal, before any write.
 */
export function planDetailsPatch(input: {
  facilityType: string | null | undefined;
  draft: Record<string, string | number | null>;
  storedTotal: number | null;
  storedSterile: number | null;
}): { ok: true; patch: Record<string, string | number | null> } | { ok: false; error: string } {
  const { draft } = input;
  const split = hasSterileSplit(input.facilityType);
  const storedSterile = split ? input.storedSterile : null;
  const numOrNullDraft = (key: string, stored: number | null): number | null => {
    if (!(key in draft)) return stored;
    const v = draft[key];
    return v == null || v === '' ? null : Number(v);
  };
  const effectiveTotal = numOrNullDraft('total_sqft', input.storedTotal);
  const effectiveSterile = numOrNullDraft('sterile_sqft', storedSterile);
  const splitTouched = 'sterile_sqft' in draft || 'total_sqft' in draft;
  const derivedNonSterile =
    effectiveTotal == null || effectiveSterile == null ? null : effectiveTotal - effectiveSterile;

  if (split && splitTouched) {
    if (effectiveSterile != null && effectiveTotal == null) {
      return {
        ok: false,
        error:
          'Enter Total sq ft first — non-sterile is derived as total − sterile, so sterile sq ft cannot be saved without a total.',
      };
    }
    if (effectiveSterile != null && effectiveTotal != null && effectiveSterile > effectiveTotal) {
      return {
        ok: false,
        error: `Sterile sq ft (${effectiveSterile.toLocaleString('en-US')}) cannot exceed total sq ft (${effectiveTotal.toLocaleString('en-US')}).`,
      };
    }
  }

  const patch: Record<string, string | number | null> = { ...draft };
  if (split && splitTouched) {
    if ('total_sqft' in draft) patch.total_sqft = effectiveTotal;
    if ('sterile_sqft' in draft) patch.sterile_sqft = effectiveSterile;
    // Recompute non-sterile whenever there is (or was) a sterile figure to
    // split against. A total edit with no sterile value leaves it untouched.
    if (effectiveSterile != null || 'sterile_sqft' in draft) {
      patch.non_sterile_sqft = derivedNonSterile;
    }
  }
  return { ok: true, patch };
}

/**
 * Save the details row, keyed on facility_id: UPDATE first, INSERT only when no
 * row matched (the superuser path; an AM has no INSERT policy on this table).
 */
export async function saveFacilityDetailsFields(
  facilityId: string,
  patch: Record<string, string | number | null>,
): Promise<{ ok: boolean; error: string | null }> {
  if (Object.keys(patch).length === 0) return { ok: true, error: null };
  const { data: updated, error: updErr } = await supabase
    .from('facility_details')
    .update(patch)
    .eq('facility_id', facilityId)
    .select('facility_id');
  if (updErr) return { ok: false, error: updErr.message };
  if (!updated || updated.length === 0) {
    const { error: insErr } = await supabase
      .from('facility_details')
      .insert({ facility_id: facilityId, ...patch });
    if (insErr) return { ok: false, error: insErr.message };
  }
  return { ok: true, error: null };
}
